/* eslint-disable */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../supabaseClient';

const fmtTime = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffDays = Math.floor((now - date) / 86400000);
  if (diffDays === 0) return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  if (diffDays === 1) return 'Hier';
  if (diffDays < 7) return date.toLocaleDateString('fr-FR', { weekday: 'short' });
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
};

const fmtDate = (s, e) => {
  if (!s) return '';
  const opts = { day: 'numeric', month: 'short' };
  return `${new Date(s).toLocaleDateString('fr-FR', opts)} → ${new Date(e).toLocaleDateString('fr-FR', opts)}`;
};

const CONTACT_PATTERNS = [
  /(\+33|0033|06|07)\s?[\d\s.\-]{8,}/,  // Téléphones FR
  /[\w.-]+@[\w.-]+\.[a-z]{2,}/,          // Emails
  /whatsapp|telegram|signal|instagram|facebook|snapchat|tiktok/i,
  /wa\.me\//i,
];

const containsContact = (text) => CONTACT_PATTERNS.some(p => p.test(text));

const maskContact = (text) => {
  let t = text;
  t = t.replace(/(\+33|0033|06|07)\s?[\d\s.\-]{8,}/g, '[numéro masqué]');
  t = t.replace(/[\w.-]+@[\w.-]+\.[a-z]{2,}/g, '[email masqué]');
  t = t.replace(/(whatsapp|telegram|signal|instagram|facebook|snapchat|tiktok)/gi, '[réseau masqué]');
  return t;
};

export default function MessagesPage({ onNavigate }) {
  const [user, setUser]           = useState(null);
  const [bookings, setBookings]   = useState([]);
  const [selected, setSelected]   = useState(null);
  const [messages, setMessages]   = useState([]);
  const [input, setInput]         = useState('');
  const [sending, setSending]     = useState(false);
  const [loading, setLoading]     = useState(true);
  const [unread, setUnread]       = useState({});
  const [isMobile, setIsMobile]   = useState(window.innerWidth < 768);
  const [showChat, setShowChat]   = useState(false);
  const [contactWarning, setContactWarning] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  // Charger les conversations (réservations actives)
  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (!session?.user) { setLoading(false); onNavigate('auth'); return; }
      const u = session.user;
      setUser(u);

      // Chercher les réservations où l'utilisateur est voyageur OU hôte
      const [{ data: asGuest }, { data: asHost }] = await Promise.all([
        supabase.from('bookings').select('*').eq('guest_id', u.id)
          .in('status', ['pending', 'confirmed', 'checkin'])
          .order('created_at', { ascending: false }),
        supabase.from('bookings').select('*').eq('host_id', u.id)
          .in('status', ['pending', 'confirmed', 'checkin'])
          .order('created_at', { ascending: false }),
      ]);

      // Fusionner et dédupliquer
      const all = [...(asGuest || []), ...(asHost || [])];
      const seen = new Set();
      const unique = all.filter(b => { if (seen.has(b.id)) return false; seen.add(b.id); return true; });

      // Enrichir avec les titres des annonces
      const listingIds = [...new Set(unique.map(b => b.listing_id).filter(Boolean))];
      let lMap = {};
      if (listingIds.length) {
        const { data: listings } = await supabase.from('listings').select('id, title, city, photos').in('id', listingIds);
        (listings || []).forEach(l => { lMap[l.id] = l; });
      }

      // Enrichir avec le dernier message
      const booksWithMeta = await Promise.all(unique.map(async b => {
        const { data: lastMsgs } = await supabase.from('messages')
          .select('*').eq('booking_id', b.id)
          .order('created_at', { ascending: false }).limit(1);
        const { data: unreadMsgs } = await supabase.from('messages')
          .select('id').eq('booking_id', b.id)
          .neq('sender_id', u.id).is('read_at', null);
        return {
          ...b,
          listing: lMap[b.listing_id] || null,
          lastMessage: lastMsgs?.[0] || null,
          unreadCount: unreadMsgs?.length || 0,
        };
      }));

      // Trier par dernier message
      booksWithMeta.sort((a, b) => {
        const da = a.lastMessage?.created_at || a.created_at;
        const db = b.lastMessage?.created_at || b.created_at;
        return new Date(db) - new Date(da);
      });

      setBookings(booksWithMeta);
      const counts = {};
      booksWithMeta.forEach(b => { if (b.unreadCount > 0) counts[b.id] = b.unreadCount; });
      setUnread(counts);
      setLoading(false);

      // Sélectionner la première conversation si desktop
      if (!isMobile && booksWithMeta.length > 0) {
        openConversation(booksWithMeta[0], u.id);
      }
    });
  }, []);

  // Charger les messages de la conversation sélectionnée
  const openConversation = useCallback(async (booking, userId) => {
    setSelected(booking);
    setMessages([]);
    if (isMobile) setShowChat(true);

    const uid = userId || user?.id;
    const { data } = await supabase.from('messages')
      .select('*').eq('booking_id', booking.id)
      .order('created_at', { ascending: true });
    setMessages(data || []);

    // Marquer comme lus
    await supabase.from('messages')
      .update({ read_at: new Date().toISOString() })
      .eq('booking_id', booking.id)
      .neq('sender_id', uid)
      .is('read_at', null);

    setUnread(prev => ({ ...prev, [booking.id]: 0 }));
    setBookings(prev => prev.map(b => b.id === booking.id ? { ...b, unreadCount: 0 } : b));
  }, [user, isMobile]);

  // Realtime
  useEffect(() => {
    if (!selected?.id) return;
    const ch = supabase.channel(`msgs:${selected.id}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages', filter: `booking_id=eq.${selected.id}` },
        (payload) => {
          setMessages(prev => prev.find(m => m.id === payload.new.id) ? prev : [...prev, payload.new]);
          if (payload.new.sender_id !== user?.id) {
            supabase.from('messages').update({ read_at: new Date().toISOString() }).eq('id', payload.new.id);
          }
        })
      .subscribe();
    return () => supabase.removeChannel(ch);
  }, [selected?.id, user?.id]);

  // Scroll auto
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || sending || !selected) return;
    if (containsContact(input)) {
      setContactWarning(true);
      setTimeout(() => setContactWarning(false), 4000);
      return;
    }
    setContactWarning(false);
    setSending(true);
    const content = input.trim();
    setInput('');
    const temp = { id: `tmp-${Date.now()}`, booking_id: selected.id, sender_id: user.id, content, created_at: new Date().toISOString() };
    setMessages(prev => [...prev, temp]);
    const { data: inserted } = await supabase.from('messages').insert({ booking_id: selected.id, sender_id: user.id, content }).select().single();
    if (inserted) setMessages(prev => prev.map(m => m.id === temp.id ? inserted : m));
    // Mettre à jour le dernier message dans la liste
    setBookings(prev => prev.map(b => b.id === selected.id ? { ...b, lastMessage: inserted || temp } : b));
    setSending(false);
  };

  const isHost = (b) => b?.host_id === user?.id;
  const otherName = (b) => isHost(b) ? (b.guest_name || `Voyageur #${b.guest_id?.slice(0,6)}`) : (b.host_name || 'Votre hôte');

  // ── RENDU ─────────────────────────────────────────────────────────────────

  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', paddingTop: 60 }}>
      <div style={{ fontSize: 14, color: 'var(--ink-3)' }}>Chargement des messages...</div>
    </div>
  );

  const totalUnread = Object.values(unread).reduce((s, n) => s + n, 0);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--white)', paddingTop: 60, display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <div style={{ borderBottom: '1px solid var(--border)', padding: '20px 24px', background: 'linear-gradient(135deg, #0F6E56 0%, #1D9E75 100%)' }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', display: 'flex', alignItems: 'center', gap: 12 }}>
          {isMobile && showChat && (
            <button onClick={() => setShowChat(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginRight: 4, color: '#fff' }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
            </button>
          )}
          <h1 style={{ fontFamily: 'var(--serif)', fontSize: 22, letterSpacing: '-0.5px', margin: 0, color: '#fff' }}>
            {isMobile && showChat && selected ? (selected.listing?.title || 'Conversation') : 'Messages'}
          </h1>
          {totalUnread > 0 && !showChat && (
            <span style={{ background: '#fff', color: 'var(--green)', fontSize: 11, fontWeight: 700, borderRadius: '50%', width: 20, height: 20, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {totalUnread}
            </span>
          )}
        </div>
      </div>

      {/* Corps */}
      <div style={{ flex: 1, maxWidth: 1100, margin: '0 auto', width: '100%', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '340px 1fr', minHeight: 0 }}>

        {/* ── LISTE CONVERSATIONS ── */}
        {(!isMobile || !showChat) && (
          <div style={{ borderRight: isMobile ? 'none' : '1px solid var(--border)', overflowY: 'auto' }}>
            {bookings.length === 0 && (
              <div style={{ padding: '3rem 2rem', textAlign: 'center' }}>
                <div style={{ fontSize: 40, marginBottom: 16 }}>💬</div>
                <p style={{ fontSize: 15, fontWeight: 500, marginBottom: 8 }}>Aucune conversation</p>
                <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 20 }}>Vos échanges avec les hôtes et voyageurs apparaîtront ici.</p>
                <button onClick={() => onNavigate('search')} style={{ padding: '10px 24px', background: 'var(--green)', color: '#fff', border: 'none', borderRadius: 20, fontSize: 14, cursor: 'pointer', fontFamily: 'var(--sans)' }}>
                  Explorer les logements →
                </button>
              </div>
            )}
            {bookings.map(b => {
              const unreadCount = b.unreadCount || 0;
              const active = selected?.id === b.id;
              const photo = b.listing?.photos?.[0];
              const role = isHost(b) ? 'Hôte' : 'Voyageur';
              return (
                <div key={b.id} onClick={() => openConversation(b, user?.id)}
                  style={{ display: 'flex', gap: 12, padding: '16px 20px', cursor: 'pointer', background: active ? 'var(--green-light)' : unreadCount > 0 ? '#FAFAF8' : 'var(--white)', borderBottom: '1px solid var(--border)', borderLeft: active ? '3px solid var(--green)' : '3px solid transparent', transition: 'background 0.15s' }}>
                  {/* Photo */}
                  <div style={{ width: 52, height: 52, borderRadius: 12, background: 'var(--surface)', flexShrink: 0, overflow: 'hidden', position: 'relative' }}>
                    {photo
                      ? <img src={photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22 }}>🏡</div>
                    }
                    {unreadCount > 0 && (
                      <div style={{ position: 'absolute', top: 2, right: 2, background: '#A32D2D', color: '#fff', fontSize: 9, fontWeight: 700, borderRadius: '50%', width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {unreadCount}
                      </div>
                    )}
                  </div>
                  {/* Infos */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                      <div style={{ fontSize: 14, fontWeight: unreadCount > 0 ? 600 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                        {b.listing?.title || `Réservation ${b.id?.slice(0,8)}`}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--ink-4)', flexShrink: 0, marginLeft: 8 }}>
                        {fmtTime(b.lastMessage?.created_at || b.created_at)}
                      </div>
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 4 }}>
                      {fmtDate(b.date_start, b.date_end)} · <span style={{ color: isHost(b) ? 'var(--green-mid)' : '#534AB7', fontWeight: 500 }}>{role}</span>
                    </div>
                    <div style={{ fontSize: 12, color: unreadCount > 0 ? 'var(--ink)' : 'var(--ink-4)', fontWeight: unreadCount > 0 ? 500 : 400, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {b.lastMessage ? (b.lastMessage.sender_id === user?.id ? 'Vous : ' : '') + b.lastMessage.content : 'Démarrer la conversation'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ── CHAT ── */}
        {(!isMobile || showChat) && (
          <div style={{ display: 'flex', flexDirection: 'column', height: isMobile ? 'calc(100vh - 120px)' : 'calc(100vh - 121px)' }}>
            {!selected ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12, color: 'var(--ink-3)' }}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
                <p style={{ fontSize: 14 }}>Sélectionnez une conversation</p>
              </div>
            ) : (
              <>
                {/* Header chat */}
                {!isMobile && (
                  <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 14 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: 'var(--surface)', overflow: 'hidden', flexShrink: 0 }}>
                      {selected.listing?.photos?.[0]
                        ? <img src={selected.listing.photos[0]} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>🏡</div>
                      }
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 500 }}>{selected.listing?.title || 'Logement'}</div>
                      <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                        {fmtDate(selected.date_start, selected.date_end)} · {isHost(selected) ? 'Mode Hôte' : 'Mode Voyageur'}
                      </div>
                    </div>
                    <button onClick={() => onNavigate('listing', { id: selected.listing_id, title: selected.listing?.title, location: selected.listing?.city, price_night: selected.price_night, photos: selected.listing?.photos, host_id: selected.host_id })}
                      style={{ fontSize: 12, color: 'var(--green-mid)', background: 'var(--green-light)', border: 'none', borderRadius: 20, padding: '5px 12px', cursor: 'pointer', fontFamily: 'var(--sans)', fontWeight: 500 }}>
                      Voir l'annonce →
                    </button>
                  </div>
                )}

                {/* Messages */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: 12, background: 'var(--surface)' }}>
                  {messages.length === 0 && (
                    <div style={{ textAlign: 'center', color: 'var(--ink-4)', fontSize: 13, marginTop: 40 }}>
                      Aucun message — démarrez la conversation 👋
                    </div>
                  )}
                  {messages.map((m, i) => {
                    const mine = m.sender_id === user?.id;
                    const showDate = i === 0 || new Date(m.created_at).toDateString() !== new Date(messages[i-1]?.created_at).toDateString();
                    return (
                      <React.Fragment key={m.id}>
                        {showDate && (
                          <div style={{ textAlign: 'center', fontSize: 11, color: 'var(--ink-4)', margin: '8px 0' }}>
                            {new Date(m.created_at).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                          </div>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start' }}>
                          <div style={{
                            maxWidth: '70%', padding: '10px 14px', borderRadius: 16,
                            borderBottomRightRadius: mine ? 4 : 16,
                            borderBottomLeftRadius: mine ? 16 : 4,
                            background: mine ? 'var(--ink)' : 'var(--white)',
                            color: mine ? '#fff' : 'var(--ink)',
                            fontSize: 14, lineHeight: 1.5,
                            boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                          }}>
                            {m.content}
                          </div>
                          <div style={{ fontSize: 10, color: 'var(--ink-4)', marginTop: 3 }}>
                            {fmtTime(m.created_at)}{mine && m.read_at ? ' · Lu' : ''}
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                  <div ref={bottomRef} />
                </div>

                {/* Alerte coordonnées */}
                {contactWarning && (
                  <div style={{ padding: '10px 16px', background: '#FDF0E6', borderTop: '1px solid #E8A87C', fontSize: 13, color: '#C4784A', display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 16 }}>⚠</span>
                    <span>Pour la sécurité des deux parties, les coordonnées personnelles ne peuvent pas être échangées via Havenly. Toutes les communications passent par la plateforme.</span>
                  </div>
                )}
                {/* Saisie */}
                <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', background: 'var(--white)', display: 'flex', gap: 10, alignItems: 'flex-end' }}>
                  <textarea
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }}}
                    placeholder="Écrire un message..."
                    rows={1}
                    style={{ flex: 1, padding: '10px 14px', border: '1px solid var(--border)', borderRadius: 20, fontSize: 14, fontFamily: 'var(--sans)', outline: 'none', resize: 'none', lineHeight: 1.5, maxHeight: 120, overflowY: 'auto' }}
                  />
                  <button onClick={sendMessage} disabled={!input.trim() || sending}
                    style={{ width: 40, height: 40, borderRadius: '50%', background: input.trim() ? 'var(--green)' : 'var(--border)', border: 'none', cursor: input.trim() ? 'pointer' : 'default', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background 0.2s' }}>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
                  </button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}